import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { useFragment, useQueryLoader } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import {
  Box,
  Text,
  VStack,
  HStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { getHoleCountString } from "../BookingRequest/components/RequestDetails/util/getHoleCountString";
import { LessonPrice, LessonPriceQueryString } from "./LessonPrice";
import { LessonPriceQuery } from "./__generated__/LessonPriceQuery.graphql";
import { RequestChargesEstimation_booking$key } from "./__generated__/RequestChargesEstimation_booking.graphql";
import { useGetRequestSummaryEstimation } from "./hooks/useGetRequestSummaryEstimation";

type Props = {
  bookingRef: RequestChargesEstimation_booking$key | null;
};

export const RequestChargesEstimation = ({ bookingRef }: Props) => {
  const booking = useFragment(
    RequestChargesEstimationFragmentString,
    bookingRef
  );

  const isLesson = booking?.__typename === "LessonBooking";
  const isLessonRequest =
    booking?.startSlot?.__typename === "QueueSlot" && isLesson;

  const [queryRef, loadQuery] = useQueryLoader<LessonPriceQuery>(
    LessonPriceQueryString
  );

  useEffect(() => {
    if (
      booking?.id &&
      booking?.durationMins &&
      booking?.rounds?.count &&
      isLessonRequest
    ) {
      loadQuery(
        {
          bookingId: booking?.id,
          duration: booking?.durationMins,
          groupSize: booking?.rounds?.count,
        },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [
    booking?.id,
    booking?.durationMins,
    booking?.rounds?.count,
    loadQuery,
    isLessonRequest,
  ]);

  const { currencySymbol } = useGetClubCurrency();

  const isPrivateLesson =
    isLesson && booking?.startSlot?.agenda?.facility?.club?.supportsPrivate;
  const isPrivateLessonRequest = isPrivateLesson && isLessonRequest;
  const isPrivateBooking =
    booking?.startSlot?.agenda?.facility?.club?.supportsPrivate ?? false;

  const { totalNineHolePrice, totalEighteenHolePrice } =
    useGetRequestSummaryEstimation({ fragmentRef: booking ?? null });

  if (isPrivateBooking) {
    return null;
  }

  const holeCount =
    booking?.rounds?.edges?.[0]?.node?.courseLayout?.holeCount ?? "EIGHTEEN";

  const totalHolePrice =
    holeCount === "NINE" ? totalNineHolePrice : totalEighteenHolePrice;

  const holeCountString = getHoleCountString(holeCount);

  return (
    <VStack backgroundColor="surface-primary" padding={5}>
      <Text variant="mainHeading">{t`Summary`}</Text>

      <Text
        variant="mainBaseMedium"
        color="text-secondary"
        marginTop={5}
      >{t`Rate Details`}</Text>

      <VStack gap={4} marginTop={5} paddingBottom={6}>
        {isPrivateLessonRequest ? (
          <HStack justifyContent="space-between">
            <Text variant="mainBaseMedium">{t`${
              booking?.lessonType?.name ?? ""
            } (${booking?.durationMins ?? 0} min)`}</Text>
            <Suspense fallback={<Loader />}>
              {queryRef ? (
                <LessonPrice queryRef={queryRef} roundsCount={1} />
              ) : null}
            </Suspense>
          </HStack>
        ) : (
          <HStack justifyContent="space-between">
            <Text variant="mainBaseMedium">{holeCountString}</Text>
            <Text
              variant="mainBaseMedium"
              testID="total-hole-price"
            >{`${currencySymbol}${ConvertCentsToDollars(
              totalHolePrice
            )}`}</Text>
          </HStack>
        )}

        <Box
          height={1}
          backgroundColor="outline-default"
          width="100%"
          style={{ marginTop: 1.5, marginBottom: 1.5 }}
        />

        <HStack justifyContent="space-between">
          <Text testID="total-expected-charges-text" variant="mainHeading">
            {t`Expected Charges`}
          </Text>

          {isPrivateLessonRequest ? (
            <Suspense fallback={<Loader />}>
              {queryRef ? (
                <LessonPrice
                  queryRef={queryRef}
                  roundsCount={booking?.rounds?.count ?? 1}
                />
              ) : null}
            </Suspense>
          ) : (
            <Text
              variant="mainHeading"
              testID="total-expected-charges"
            >{`${currencySymbol}${ConvertCentsToDollars(
              totalHolePrice * (booking?.rounds?.count ?? 1)
            )}`}</Text>
          )}
        </HStack>
        <Text
          color="text-secondary"
          variant="mainBaseMedium"
        >{t`These rates are an estimate and may vary based upon your actual scheduled booking.`}</Text>
      </VStack>
    </VStack>
  );
};

export const RequestChargesEstimationFragmentString = graphql`
  fragment RequestChargesEstimation_booking on Booking {
    id
    __typename
    ... on StandardBooking {
      durationMins
    }
    ... on LessonBooking {
      lessonType {
        name
      }
      durationMins
    }
    startSlot {
      __typename
      agenda {
        facility {
          club {
            supportsPrivate
          }
        }
      }
    }
    rounds {
      count
      edges {
        node {
          courseLayout {
            holeCount
          }
        }
      }
    }
    ...useGetRequestSummaryEstimation_Booking
  }
`;
